import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, AlertTriangle, CheckCircle, FileCode, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";

interface HardcodedString {
  file: string;
  line: number;
  column: number;
  text: string;
  context: string;
}

interface ScanResult {
  totalFiles: number;
  filesWithIssues: number;
  totalIssues: number;
  issues: HardcodedString[];
}

const TranslationChecker = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  const handleScan = async () => {
    setScanning(true);
    try {
      const { data, error } = await supabase.functions.invoke('scan-hardcoded-strings');

      if (error) throw error;

      setResult(data);
      toast({
        title: t('admin.scan_complete'),
        description: `${data.totalIssues} ${t('admin.issues_found')}`,
      });
    } catch (error) {
      console.error('Error scanning:', error);
      toast({
        title: t('common.error'),
        description: t('admin.scan_error'),
        variant: "destructive",
      });
    } finally {
      setScanning(false);
    }
  };

  const filteredIssues = selectedFile
    ? result?.issues.filter(issue => issue.file === selectedFile)
    : result?.issues;

  const fileList = result?.issues.reduce((acc, issue) => {
    if (!acc.find(f => f.file === issue.file)) {
      const count = result.issues.filter(i => i.file === issue.file).length;
      acc.push({ file: issue.file, count });
    }
    return acc;
  }, [] as { file: string; count: number }[]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            {t('admin.translation_checker')}
          </CardTitle>
          <CardDescription>
            {t('admin.translation_checker_description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleScan}
            disabled={scanning}
            className="w-full sm:w-auto"
          >
            {scanning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('admin.scanning')}
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                {t('admin.start_scan')}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <FileCode className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <div className="text-2xl font-bold">{result.totalFiles}</div>
                  <p className="text-xs text-muted-foreground">{t('admin.files_scanned')}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-warning" />
                  <div className="text-2xl font-bold">{result.filesWithIssues}</div>
                  <p className="text-xs text-muted-foreground">{t('admin.files_with_issues')}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-success" />
                  <div className="text-2xl font-bold">
                    {result.totalFiles - result.filesWithIssues}
                  </div>
                  <p className="text-xs text-muted-foreground">{t('admin.files_ok')}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-12">
            {/* File List */}
            <Card className="md:col-span-4">
              <CardHeader>
                <CardTitle className="text-base">{t('admin.affected_files')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {fileList?.map(({ file, count }) => (
                      <button
                        key={file}
                        onClick={() => setSelectedFile(selectedFile === file ? null : file)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedFile === file
                            ? 'bg-primary/10 border-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-mono truncate">{file}</span>
                          <Badge variant="destructive" className="ml-2">
                            {count}
                          </Badge>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Issues List */}
            <Card className="md:col-span-8">
              <CardHeader>
                <CardTitle className="text-base">
                  {selectedFile ? t('admin.issues_in_file') : t('admin.all_issues')}
                </CardTitle>
                {selectedFile && (
                  <CardDescription className="font-mono text-xs">
                    {selectedFile}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {filteredIssues?.map((issue, index) => (
                      <div
                        key={`${issue.file}-${issue.line}-${index}`}
                        className="p-4 border rounded-lg space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-muted-foreground">
                            {issue.file}:{issue.line}:{issue.column}
                          </span>
                          <Badge variant="outline">
                            Line {issue.line}
                          </Badge>
                        </div>
                        <div className="p-3 bg-muted rounded-md">
                          <code className="text-sm">{issue.text}</code>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <span className="font-semibold">Context:</span>
                          <pre className="mt-1 p-2 bg-background rounded overflow-x-auto">
                            {issue.context}
                          </pre>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default TranslationChecker;
